import { useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter } from "react-router-dom";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/components/auth/AuthProvider";
import AppRoutes from "./components/routes/AppRoutes";
import Navigation from "./components/Navigation";
import { NuqsAdapter } from "nuqs/adapters/react";
import { usePageTracking } from "@/hooks/usePageTracking";
import { WhatsAppBubble } from "@/components/WhatsAppBubble";
import { VersionBadge } from "@/components/admin/VersionBadge";
import { UpdateNotification } from "@/components/UpdateNotification";
import { InteractionTracker } from "@/components/tracking/InteractionTracker";

const queryClient = new QueryClient();

// Analytics tracker component (page views for GA/Meta)
const AppTrackers = () => {
  usePageTracking();
  return null;
};

// Global error handler to prevent unhandled promise rejections from crashing the app
const GlobalErrorHandler = () => {
  useEffect(() => {
    const handleRejection = (event: PromiseRejectionEvent) => {
      console.error("Unhandled promise rejection:", event.reason);
      event.preventDefault();
    };

    window.addEventListener("unhandledrejection", handleRejection);
    return () => window.removeEventListener("unhandledrejection", handleRejection);
  }, []);

  return null;
};

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <BrowserRouter basename="/">
          <NuqsAdapter>
            <AuthProvider>
              <InteractionTracker>
                <GlobalErrorHandler />
                <AppTrackers />
                <div className="pb-20">
                  <AppRoutes />
                </div>
                <Navigation />
                <WhatsAppBubble />
                <VersionBadge />
                <UpdateNotification />
              </InteractionTracker>
            </AuthProvider>
            <Toaster />
            <Sonner />
          </NuqsAdapter>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
